/**
 * Substrate × calculation substrate fetcher for Atlas Day 2 Phase 4.
 *
 * Single async function fetches substrate-driven counts + dial values
 * for the 3 flat regions within the substrate × calculation grid cell:
 *
 *   Business defaults    — params (3 key dial values: min_roi_pct,
 *                          returns_reserve_pct, default_lead_time_days)
 *                          per scope §6.2
 *   Path mechanics       — fpc total + active + path_type distribution
 *                          per scope §6.3
 *   Weight components    — bswc catalog total + active count + F33 trap
 *                          row count per scope §6.4 + Phase 0 #11
 *
 * Uses arbi-dev's own supabase client. No caching at Day 2; direct fetch
 * on region mount. D1 (α) lock applied — Weight components surfaces
 * catalog only (no weight values; weights live per-session per scope §2).
 */

import { supabase } from '../supabase';

// ── Types ────────────────────────────────────────────────────────────────────

export interface BusinessDefaultsSubstrate {
  minRoiPct: number | null;
  returnsReservePct: number | null;
  defaultLeadTimeDays: number | null;
}

export interface PathMechanicsSubstrate {
  total: number;
  active: number;
  /** Counts by path_type, e.g., { merchant_managed: 4, fulfillment_service_managed: 2, dropship: 1 } */
  byType: Record<string, { total: number; active: number }>;
}

export interface WeightComponentsSubstrate {
  total: number;
  active: number;
  /** F33 trap: rows with is_available=true AND applies_to_metric IS NULL */
  f33TrapRows: number;
}

export interface SubstrateCalcSubstrate {
  businessDefaults: BusinessDefaultsSubstrate;
  pathMechanics: PathMechanicsSubstrate;
  weightComponents: WeightComponentsSubstrate;
}

// ── Fetcher ──────────────────────────────────────────────────────────────────

export async function fetchSubstrateCalcSubstrate(): Promise<SubstrateCalcSubstrate> {
  const [paramsRes, fpcRes, bswcRes] = await Promise.all([
    supabase
      .from('params')
      .select('key, value')
      .eq('level', 'business')
      .is('entity_id', null)
      .in('key', ['min_roi_pct', 'returns_reserve_pct', 'default_lead_time_days']),
    supabase.from('fulfillment_path_config').select('path_type, is_active'),
    supabase
      .from('buy_score_weight_components')
      .select('is_available, applies_to_metric'),
  ]);

  const params = (paramsRes.data ?? []) as Array<{ key: string; value: unknown }>;
  const readParam = (key: string): number | null => {
    const row = params.find((p) => p.key === key);
    if (!row) return null;
    return typeof row.value === 'number' ? row.value : Number(row.value);
  };

  const fpcRows = (fpcRes.data ?? []) as Array<{ path_type: string | null; is_active: boolean }>;
  const byType: Record<string, { total: number; active: number }> = {};
  for (const row of fpcRows) {
    const type = row.path_type ?? 'unknown';
    if (!byType[type]) byType[type] = { total: 0, active: 0 };
    byType[type].total += 1;
    if (row.is_active) byType[type].active += 1;
  }

  const bswcRows = (bswcRes.data ?? []) as Array<{
    is_available: boolean;
    applies_to_metric: string | null;
  }>;
  const bswcActive = bswcRows.filter((r) => r.is_available).length;
  const f33TrapRows = bswcRows.filter((r) => r.is_available && r.applies_to_metric == null).length;

  return {
    businessDefaults: {
      minRoiPct: readParam('min_roi_pct'),
      returnsReservePct: readParam('returns_reserve_pct'),
      defaultLeadTimeDays: readParam('default_lead_time_days'),
    },
    pathMechanics: {
      total: fpcRows.length,
      active: fpcRows.filter((r) => r.is_active).length,
      byType,
    },
    weightComponents: {
      total: bswcRows.length,
      active: bswcActive,
      f33TrapRows,
    },
  };
}
